import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cx } from './cx';
import IconButton from './IconButton';
import { useModalMotion } from '@/hooks/useModalMotion';
import { isTopOverlay, lockScroll, nextOverlaySeq, popOverlay, pushOverlay, unlockScroll } from './overlayStack';
import { useFocusTrap } from './useFocusTrap';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Dialog heading; becomes the accessible name. */
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  /** Actions; secondary first, primary last (stacked full width on phones). */
  footer?: React.ReactNode;
  /** 27.5 / 32.5 / 40 / 47.5rem (440 / 520 / 640 / 760px at scale 1). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** `auto` = bottom sheet below 640px, centred above. */
  placement?: 'auto' | 'center';
  /** false = no close button, Escape and backdrop do nothing. */
  dismissible?: boolean;
  /** Backdrop click closes (only when dismissible). Default false. */
  closeOnBackdrop?: boolean;
  /** Accessible name of the close button (translated). */
  closeLabel?: string;
  /** false = body sizes to content and does not scroll. */
  scrollBody?: boolean;
  initialFocus?: React.RefObject<HTMLElement | null>;
  className?: string;
}

const WIDTH = { sm: '@bp-sm:max-w-[27.5rem]', md: '@bp-sm:max-w-[32.5rem]', lg: '@bp-sm:max-w-[40rem]', xl: '@bp-sm:max-w-[47.5rem]' } as const;

interface FrameProps extends DialogProps {
  role: 'dialog' | 'alertdialog';
  layer: 'dialog' | 'blocking';
}

/** Shared frame for Dialog and AlertDialog. */
export function DialogFrame({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  placement = 'auto',
  dismissible = true,
  closeOnBackdrop = false,
  closeLabel = 'Close',
  scrollBody = true,
  initialFocus,
  className,
  role,
  layer,
}: FrameProps) {
  const { render, exiting, backdropClass, panelClass } = useModalMotion(open);
  const id = useId();
  const titleId = title != null ? `${id}-title` : undefined;
  const descId = description != null ? `${id}-desc` : undefined;
  const rootRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Stack order is fixed when the dialog opens (render order: parents first).
  const seqRef = useRef(0);
  if (open && seqRef.current === 0) seqRef.current = nextOverlaySeq();
  if (!open && !render) seqRef.current = 0;
  const active = open && render;

  useEffect(() => {
    if (!active) return;
    pushOverlay(id, seqRef.current, rootRef.current, layer);
    lockScroll();
    return () => {
      popOverlay(id);
      unlockScroll();
    };
  }, [active, id, layer]);

  useEffect(() => {
    if (!open || !dismissible) return;
    const onKey = (e: KeyboardEvent) => {
      // A popover inside the dialog (Select, Menu) handles Escape first and marks it handled.
      if (e.key === 'Escape' && !e.defaultPrevented && isTopOverlay(id)) {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, dismissible, id]);

  useFocusTrap(rootRef, active, id, initialFocus);

  if (!render) return null;
  const sheet = placement === 'auto';

  return createPortal(
    <div
      ref={rootRef}
      role={role}
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      tabIndex={-1}
      className={cx(
        'fixed inset-0 flex justify-center focus:outline-none',
        layer === 'blocking' ? 'z-blocking' : 'z-dialog',
        sheet ? 'items-end p-0 @bp-sm:items-center @bp-sm:p-4' : 'items-center p-4'
      )}
    >
      <div
        aria-hidden
        className={cx('absolute inset-0 bg-scrim backdrop-blur-sm', backdropClass)}
        onClick={dismissible && closeOnBackdrop && !exiting ? onClose : undefined}
      />
      <div
        className={cx(
          'relative flex w-full min-h-0 flex-col overflow-hidden border k-panel bg-raised text-primary',
          'max-h-[min(88dvh,calc(100dvh-2rem))]',
          sheet ? 'rounded-t-overlay @bp-sm:rounded-overlay' : 'rounded-overlay',
          WIDTH[size],
          panelClass,
          className
        )}
      >
        {title != null || dismissible ? (
          <div className="flex shrink-0 items-start justify-between gap-3 px-5 pb-3 pt-4">
            <div className="flex min-w-0 flex-col gap-1 pt-2">
              {title != null ? (
                <h2 id={titleId} className="m-0 font-display k-heading text-title text-primary">
                  {title}
                </h2>
              ) : null}
              {description != null ? (
                <p id={descId} className="m-0 font-ui text-small text-secondary">
                  {description}
                </p>
              ) : null}
            </div>
            {dismissible ? <IconButton label={closeLabel} icon={<X />} onClick={() => !exiting && onClose()} /> : null}
          </div>
        ) : null}
        <div
          className={cx(
            'min-h-0 px-5 font-ui text-body text-secondary',
            footer ? 'pb-4' : 'pb-[calc(1rem+env(safe-area-inset-bottom,0px))] @bp-sm:pb-4',
            scrollBody ? 'flex-1 overflow-y-auto overscroll-contain cosmos-scroll-y' : 'shrink-0'
          )}
        >
          {children}
        </div>
        {footer ? (
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-edge-subtle px-5 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pt-4 @bp-sm:flex-row @bp-sm:justify-end @bp-sm:pb-4 [&>*]:w-full @bp-sm:[&>*]:w-auto">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

/** The one dialog: focus trap, Escape, scroll lock, stacking, bottom sheet on phones. */
export default function Dialog(props: DialogProps) {
  return <DialogFrame {...props} role="dialog" layer="dialog" />;
}
